import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { updateDealFolder } from '../../../../api/mutations';
import { getDealFolders } from '../../../../api/queries_v2';
import { useNotifications } from '../../../../notifications';
import PropdocsModal from '../../../../components/modal/PropdocsModal';

interface RenameDealFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  currentName: string;
  teamId: string;
  onSuccess: () => void;
}

const RenameDealFolderModal: React.FC<RenameDealFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  currentName,
  teamId,
  onSuccess,
}) => {
  const { formatMessage } = useIntl();
  const [folderName, setFolderName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Reset form when modal opens with new folder
  useEffect(() => {
    if (isOpen) {
      setFolderName(currentName);
      setErrorMessage(null);
    }
  }, [isOpen, currentName]);

  const [renameFolder] = useMutation(updateDealFolder, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder renamed successfully' });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      console.error('Error renaming folder:', error);
      if (error.message.includes('already exists')) {
        setErrorMessage('A folder with this name already exists');
      } else {
        setErrorMessage(error.message || 'Failed to rename folder');
      }
      setIsSubmitting(false);
    },
  });

  const handleClose = () => {
    setFolderName(currentName);
    setIsSubmitting(false);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = folderName.trim();
    if (!trimmedName) {
      setErrorMessage('Folder name cannot be empty');
      return;
    }

    if (trimmedName === currentName) {
      handleClose();
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await renameFolder({
        variables: {
          id: folderId,
          name: trimmedName,
        },
        refetchQueries: [
          {
            query: getDealFolders,
            variables: { teamId },
          },
        ],
      });
    } catch (error) {
      // Error handling is done in the onError callback
      console.error('Rename folder error:', error);
    }
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isSubmitting}
      title={<FormattedMessage id="deals.folders.rename" defaultMessage="Rename Folder" />}
    >
      <>
        <div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="folderName" className="form-label">
                <FormattedMessage id="deals.folders.name" defaultMessage="Folder Name" />
              </label>
              <input
                type="text"
                id="folderName"
                className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                  setErrorMessage(null);
                }}
                placeholder={formatMessage({
                  id: 'deals.folders.name.placeholder',
                  defaultMessage: 'Enter folder name',
                })}
                disabled={isSubmitting}
                autoFocus
              />
              {errorMessage && <div className="invalid-feedback d-block">{errorMessage}</div>}
            </div>
          </form>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!folderName.trim() || folderName.trim() === currentName || isSubmitting}
          >
            {isSubmitting ? (
              <FormattedMessage id="common.renaming" defaultMessage="Renaming..." />
            ) : (
              <FormattedMessage id="deals.folders.rename" defaultMessage="Rename" />
            )}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default RenameDealFolderModal;
